import {
  CLASS_TIME_PER_WEEK_OPTIONS,
  ENTRY_TYPES,
  INDUSTRY_AREA_OPTIONS,
  INTENT_OPTIONS,
  MATCH_FLEXIBILITY_OPTIONS,
  PROGRAM_TYPE_OPTIONS,
  ROLE_OPTIONS,
  USAGE_FREQUENCY_OPTIONS,
} from "./ai-curriculum.constants";

export const findOption = (options, id) =>
  options.find((o) => o.id === id) ?? null;

export const defaultCriteria = () => ({
  // State Standards / Upload path.
  programType: null,
  usageFrequency: null,
  matchFlexibility: findOption(MATCH_FLEXIBILITY_OPTIONS, "exact"),
  // Quick Start path.
  intent: null,
  industryArea: null,
  industrySpecifics: "",
  role: null,
  classTimePerWeek: null,
  programDescription: "",
});

// Inverse of the build*Payload functions below: turns the ids a session's
// stored criteria come back as (ENG-23488) into the option objects the form
// binds to. Only the keys the entry path submitted are present, so anything
// missing keeps its default.
const WIRE_OPTION_FIELDS = [
  ["intent", INTENT_OPTIONS],
  ["industryArea", INDUSTRY_AREA_OPTIONS],
  ["role", ROLE_OPTIONS],
  ["classTimePerWeek", CLASS_TIME_PER_WEEK_OPTIONS],
  ["programType", PROGRAM_TYPE_OPTIONS],
  ["usageFrequency", USAGE_FREQUENCY_OPTIONS],
  ["matchFlexibility", MATCH_FLEXIBILITY_OPTIONS],
];

export const criteriaFromWire = (wire) => {
  if (!wire) return null;

  const criteria = defaultCriteria();
  for (const [key, options] of WIRE_OPTION_FIELDS) {
    if (wire[key] != undefined) criteria[key] = findOption(options, wire[key]);
  }
  for (const key of ["industrySpecifics", "programDescription"]) {
    if (typeof wire[key] === "string") criteria[key] = wire[key];
  }
  return criteria;
};

// Maps the analyzer's suggestedCriteria (GET /status → analyzerSummary) onto
// a criteria patch for the Upload path form. Missing suggestions stay null
// (the user picks manually). Match Flexibility is deliberately not part of
// the patch: it keeps the shared default (exact), consistent across all
// three entry points (ENG-23283).
export const syllabusCriteriaFromAnalyzer = (analyzerSummary) => {
  const suggested = analyzerSummary?.suggestedCriteria ?? {};
  return {
    industryArea: findOption(INDUSTRY_AREA_OPTIONS, suggested.industryArea),
    programType: findOption(PROGRAM_TYPE_OPTIONS, suggested.programType),
    usageFrequency: findOption(
      USAGE_FREQUENCY_OPTIONS,
      suggested.usageFrequency
    ),
  };
};

// Prefills the Quick Start form from a convert-to-quick-start response's
// suggestedCriteria without clobbering anything the user already picked
// (ENG-23217): only empty fields take the suggestion.
export const quickStartCriteriaFromSuggestions = (criteria, suggested) => {
  if (!suggested) return criteria;
  return {
    ...criteria,
    industryArea:
      criteria.industryArea ??
      findOption(INDUSTRY_AREA_OPTIONS, suggested.industryArea),
  };
};

// Validates criteria for the paths that collect an industry area (Quick Start
// and Upload). The required dropdowns are gated by the submit button via
// hasRequiredCriteria; the only submit-time rule left is the conditional one:
// industrySpecifics is required when industryArea is "other" (mirrors the
// backend _other_requires_industry_specifics validator).
export const validateQuickStartCriteria = (criteria) => {
  const errors = {};
  if (
    criteria.industryArea?.id === "other" &&
    !criteria.industrySpecifics.trim()
  ) {
    errors.industrySpecifics =
      "Please describe your industry area when choosing Other.";
  }
  return errors;
};

// Whether the required dropdowns for the active entry type are all selected —
// gates the "Generate Recommendations" button.
export const hasRequiredCriteria = (criteria, entryType, standardsContext) => {
  if (entryType === ENTRY_TYPES.QUICK_START) {
    return (
      !!criteria.intent &&
      !!criteria.industryArea &&
      !!criteria.role &&
      !!criteria.classTimePerWeek
    );
  }
  if (entryType === ENTRY_TYPES.SYLLABUS_UPLOAD) {
    return (
      !!criteria.industryArea &&
      !!criteria.programType &&
      !!criteria.usageFrequency &&
      !!criteria.matchFlexibility
    );
  }
  // State Standards path.
  return (
    !!criteria.programType &&
    !!criteria.usageFrequency &&
    !!criteria.matchFlexibility &&
    !!standardsContext
  );
};

// Builds the Quick Start criteria payload (ENG-23102). Free-text fields are
// trimmed and omitted when empty — the backend rejects blank-but-present
// strings.
export const buildQuickStartPayload = (criteria, overrides) => {
  const specifics = criteria.industrySpecifics.trim();
  const description = criteria.programDescription.trim();
  return {
    intent: criteria.intent.id,
    industryArea: criteria.industryArea.id,
    ...(specifics ? { industrySpecifics: specifics } : {}),
    role: criteria.role.id,
    classTimePerWeek: criteria.classTimePerWeek.id,
    ...(description ? { programDescription: description } : {}),
    matchFlexibility: criteria.matchFlexibility.id,
    ...(overrides ? { overrides } : {}),
  };
};

// Builds the Upload Syllabus criteria payload (ENG-23218). The recommender's
// retrieval is syllabus-driven server-side (embedded objectives/standards);
// these fields tune the prompt the same way the other paths do.
export const buildSyllabusUploadPayload = (criteria, overrides) => {
  const specifics = criteria.industrySpecifics.trim();
  return {
    industryArea: criteria.industryArea.id,
    ...(specifics ? { industrySpecifics: specifics } : {}),
    programType: criteria.programType.id,
    usageFrequency: criteria.usageFrequency.id,
    matchFlexibility: criteria.matchFlexibility.id,
    ...(overrides ? { overrides } : {}),
  };
};

// Builds the State Standards criteria payload. The industry-pathway
// hint uses the most specific pick: framework drill-down if the user chose one,
// otherwise the subject.
export const buildStandardsPayload = (
  criteria,
  standardsContext,
  overrides
) => {
  const pathway = standardsContext?.sourceName || standardsContext?.subject;
  return {
    programType: criteria.programType.id,
    usageFrequency: criteria.usageFrequency.id,
    matchFlexibility: criteria.matchFlexibility.id,
    ...(pathway ? { industryPathways: [pathway] } : {}),
    ...(overrides ? { overrides } : {}),
  };
};
